import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Layout } from '@/components/Layout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ApiKeysPage } from '@/pages/ApiKeysPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { supabase } from '@/lib/supabase'
import { Toaster } from '@/components/ui/sonner'

function App() {
  const { setUser, setLoading } = useAuthStore()
  const { setTheme } = useThemeStore()

  useEffect(() => {
    console.log('App initializing...')

    // Initialize theme
    setTheme('dark')
    console.log('Theme set to dark')

    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('Getting initial session...')
        const {
          data: { session },
        } = await supabase.auth.getSession()
        console.log('Session data:', session)
        setUser(session?.user ?? null)
        console.log('User set in store')
      } catch (error) {
        console.error('Error getting session:', error)
        setUser(null)
      } finally {
        setLoading(false)
        console.log('Loading set to false')
      }
    }

    getInitialSession()

    // Listen for auth changes
    console.log('Setting up auth state change listener...')
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('Auth state changed:', session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => {
      console.log('Cleaning up auth listener')
      subscription.unsubscribe()
    }
  }, [setUser, setLoading, setTheme])

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path='/login' element={<LoginPage />} />
          <Route
            path='/'
            element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <DashboardPage />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path='/api-keys'
            element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <ApiKeysPage />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path='/settings'
            element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <SettingsPage />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path='/dashboard' element={<Navigate to='/' replace />} />
        </Routes>
      </Router>
      <Toaster />
    </ErrorBoundary>
  )
}

export default App
